import {
    deleteRusSubtitleApi,
    downloadRusAudioApi,
    editRusSubtitleApi,
    editTranslatedSubtitleApi,
    getAllDataForEditingApi,
    getFoldersApi,
    mergeRusSubtitleApi,
    splitRusSubtitleApi,
    translateSubtitleApi,
    syntAudioApi,
    getTranslatedSubtitleRecommendsApi,
    deleteTranslatedSubtitle,
    deleteAudioApi,
    getSubtitleRecommendsApi,
    createRusSubIntervalApi,
    createRusSubIntervalMultiApi,
    cutAudioWithoutPause,
    editMassTranslatedSubtitleApi,
    materializeClipApi,
    splitTranslatedSubtitleApi,
    opusSplitTranslatedSubtitleApi,
    mergeTranslatedSubtitlesApi,
    editMasterRuSubtitleApi,
    recreateTranslatedFromRuApi,
    ruDoneGetApi,
    ruDoneSetApi,
    translationDoneGetApi,
    translationDoneSetApi,
} from "api/fastApi";
import { errorHandler } from "../../utils/errorHandle";
import { errorReason } from "../../api/fastApi/httpErrors";
import {
    CreateRusSubIntervalArgs,
    CreateRusSubIntervalMultiArgs,
    CreateRusSubIntervalMultiResponse,
    CreateRusSubIntervalResponse,
    CutAudioArgs,
    CutAudioResponse,
    DeleteAudioArgs,
    DeleteSubtitleArgs,
    DeleteTranslatedSubtitleArgs,
    EditMassTranslatedSubtitleArgs,
    EditMassTranslatedSubtitleResponse,
    EditMasterRuSubtitleArgs,
    EditSubtitleArgs,
    EditTranslatedSubtitleArgs,
    GetRecommendationArgs,
    MergeSubtitleArgs,
    OpusSplitTranslatedSubtitleArgs,
    RecommendationArgs,
    RecreateTranslatedFromRuArgs,
    SplitSubtitleArgs,
    SplitTranslatedSubtitleArgs,
    SyntAudioArgs,
    TranslateSubtitleArgs,
} from "../../api/fastApi/types";
import { ILang, MetadataToSubtitlesLanguages } from "./types";
import { serverMergedTargets } from "./mergeResponse";

export const getAllSubtitlesAction = async ({
    mediaId,
    lang,
    languageFrom,
}: {
    mediaId: string;
    lang: string;
    languageFrom?: string;
}) => {
    let error = "get all data error";
    try {
        // Cut-videos clip: the editor opens it with ?clip={clip_id}. Materialize the clip on disk
        // (download its subs + audio from the CDN, idempotent/cached) BEFORE reading the editor data,
        // since clips live only on R2 and have no on-disk project folder until now. Strictly gated by
        // the clip param — a normal lecture has no `clip` and this is skipped entirely.
        const clip = new URLSearchParams(window.location.search).get("clip");
        if (clip) {
            try {
                // Materialize the language being edited (ch -> zh for the backend); default ru. The
                // source is what it was translated FROM — languageFrom when editing a translation, else
                // the edited language itself. Backend falls back to RU if the source isn't published.
                const clipLang = lang === "ch" ? "zh" : lang || "ru";
                const clipSource = languageFrom || (clipLang === "en" ? "en" : "ru");
                await materializeClipApi(clip, clipLang, clipSource);
            } catch (e) {
                // If materialization fails but the folder already exists, the editor still loads.
            }
        }
        const data = await getAllDataForEditingApi({ mediaId, lang, languageFrom });
        return { data };
    } catch (err) {
        const error = errorHandler(err as Error);
    }
    return { error };
};

export const getFoldersAction = async (mediaId: string) => {
    let error = "get Folders data error";

    try {
        const data = await getFoldersApi(mediaId);
        return { data };
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error };
};

export const editRusSubtitleAction = async ({ mediaId, ...body }: EditSubtitleArgs) => {
    let error = "edit rus subtitle data error";
    try {
        return await editRusSubtitleApi({ mediaId, ...body });
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error };
};

export const editTranslatedSubtitleAction = async ({
    mediaId,
    ...body
}: EditTranslatedSubtitleArgs) => {
    let error = "edit translated subtitle data error";
    try {
        return await editTranslatedSubtitleApi({ mediaId, ...body });
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error };
};

export const editMassTranslatedSubtitleAction = async ({
    mediaId,
    body,
    origin,
}: EditMassTranslatedSubtitleArgs) => {
    let error = "edit mass translated subtitle data error";
    let data: EditMassTranslatedSubtitleResponse | undefined;
    try {
        data = await editMassTranslatedSubtitleApi({ mediaId, body, ...(origin ? { origin } : {}) });
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error, data };
};

export const getDownloadedRusAudioUrl = async (fileName: string) => {
    let error = "downloadRusAudio error";
    try {
        const blob = await downloadRusAudioApi(fileName);
        const url = window.URL.createObjectURL(blob);
        return { data: url };
    } catch (err) {
        const error = errorHandler(err as Error);
    }
    return { error };
};

export const splitRusSubtitleAction = async ({ mediaId, ...body }: SplitSubtitleArgs) => {
    let error = "split rus subtitle data error";
    try {
        const data = await splitRusSubtitleApi({ mediaId, ...body });
        console.log("splitRusSubtitleAction", data);
        return { data };
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error };
};

export const mergeRusSubtitleAction = async ({ mediaId, ...body }: MergeSubtitleArgs) => {
    let error = "merge rus subtitle data error";
    try {
        const data = await mergeRusSubtitleApi({ mediaId, ...body });
        return { data };
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error };
};

// The translated-led actions surface the error's own message (errorHandler returns
// err.message), so the backend 409/422 texts reach the UI verbatim.

export const splitTranslatedSubtitleAction = async ({
    mediaId,
    ...body
}: SplitTranslatedSubtitleArgs) => {
    let error = "split translated subtitle data error";
    try {
        const data = await splitTranslatedSubtitleApi({ mediaId, ...body });
        return { data };
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error };
};

export const opusSplitTranslatedSubtitleAction = async ({
    mediaId,
    ...body
}: OpusSplitTranslatedSubtitleArgs) => {
    let error = "opus split translated subtitle data error";
    try {
        const data = await opusSplitTranslatedSubtitleApi({ mediaId, ...body });
        return { data };
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error };
};

export const mergeTranslatedSubtitlesAction = async ({ mediaId, ...body }: MergeSubtitleArgs) => {
    let error = "merge translated subtitles data error";
    try {
        const data = await mergeTranslatedSubtitlesApi({ mediaId, ...body });
        return { data };
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error };
};

export const editMasterRuSubtitleAction = async ({
    mediaId,
    ...body
}: EditMasterRuSubtitleArgs) => {
    let error = "edit master ru subtitle data error";
    try {
        const data = await editMasterRuSubtitleApi({ mediaId, ...body });
        return { data };
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error };
};

export const recreateTranslatedFromRuAction = async ({
    mediaId,
    ...body
}: RecreateTranslatedFromRuArgs) => {
    let error = "recreate translated from ru data error";
    try {
        const data = await recreateTranslatedFromRuApi({ mediaId, ...body });
        return { data };
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error };
};

export const ruDoneSetAction = async ({ mediaId, ...body }: import("api/fastApi/types").RuDoneArgs) => {
    try {
        const data = await ruDoneSetApi({ mediaId, ...body });
        return { data };
    } catch (err) {
        return { error: errorHandler(err as Error) };
    }
};

export const ruDoneGetAction = async (mediaId: string) => {
    try {
        const data = await ruDoneGetApi(mediaId);
        return { data };
    } catch (err) {
        return { error: errorHandler(err as Error) };
    }
};

export const translationDoneSetAction = async ({
    mediaId,
    ...body
}: import("api/fastApi/types").TranslationDoneArgs) => {
    try {
        const data = await translationDoneSetApi({ mediaId, ...body });
        return { data };
    } catch (err) {
        return { error: errorHandler(err as Error) };
    }
};

export const translationDoneGetAction = async (
    mediaId: string,
    lang: string,
    languageFrom = "ru",
) => {
    try {
        const data = await translationDoneGetApi(mediaId, lang, languageFrom);
        return { data };
    } catch (err) {
        return { error: errorHandler(err as Error) };
    }
};

export const deleteRusSubtitleAction = async ({ mediaId, ...body }: DeleteSubtitleArgs) => {
    let error = "delete rus subtitle data error";
    try {
        return await deleteRusSubtitleApi({ mediaId, ...body });
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error };
};

/** Машинный перевод одной реплики: ответ или отказ словами и с кодом (translation_exists…). */
export const translateSubtitleAction = async ({
    mediaId,
    ...body
}: TranslateSubtitleArgs): Promise<{ data?: unknown; error?: string; reason?: string }> => {
    try {
        const data = await translateSubtitleApi({ mediaId, ...body });
        return { data };
    } catch (err) {
        return { error: errorHandler(err as Error), reason: errorReason(err) };
    }
};

export const syntAudioAction = async (params: SyntAudioArgs) => {
    let error = "synt audio error";
    try {
        return await syntAudioApi(params);
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error };
};

export const getTranslatedSubtitleRecommendsAction = async (params: RecommendationArgs) => {
    try {
        const data = await getTranslatedSubtitleRecommendsApi(params);
        return { data, error: null };
    } catch (err) {
        return { data: null, error: errorHandler(err as Error) };
    }
};

export const getSubtitleRecommendsAction = async (params: GetRecommendationArgs) => {
    try {
        const data = await getSubtitleRecommendsApi(params);
        return { data, error: null };
    } catch (err) {
        return { data: null, error: errorHandler(err as Error) };
    }
};

export const deleteTranslatedSubtitleAction = async (params: DeleteTranslatedSubtitleArgs) => {
    let error = "delete Translated Subtitle audio error";
    try {
        return await deleteTranslatedSubtitle(params);
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error };
};

export const deleteAudioAction = async (params: DeleteAudioArgs) => {
    let error = "delete audio error";
    try {
        await deleteAudioApi(params);
        return { data: "ok" };
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error };
};

export const mergeSubtitles = async ({
    mediaId,
    folder_num,
    id,
    lang,
    language_from,
}: MergeSubtitleArgs & { lang: MetadataToSubtitlesLanguages }) => {
    const data = await mergeRusSubtitleAction({ mediaId, folder_num, id, lang, language_from });
    if (data.error) {
        return { error: data.error };
    }
    // Новый сервер сам склеил перевод и сам убрал старые файлы (поле merged_translations в ответе).
    // Удалять следом тогда нечего — а у старого сервера эти удаления уносили в delete/ перевод
    // и озвучку соседа, если перевода не было у второй реплики. Со старым сервером — как раньше.
    if (serverMergedTargets(data.data)) {
        return data;
    }
    // Every one of these used to omit language_from, so the backend fell back to "ru" and, on
    // an en_ar or en_ch lecture, cleaned up in a pair that lecture does not have.
    await Promise.all([
        deleteTranslatedSubtitleAction({
            mediaId,
            id: id[0],
            lang,
            folder_num,
            language_from,
        }),
        deleteTranslatedSubtitleAction({
            mediaId,
            id: id[1],
            lang,
            folder_num,
            language_from,
        }),
        deleteAudioAction({
            mediaId,
            folder_num,
            lang,
            id: id[0],
            language_from,
        }),
        deleteAudioAction({
            mediaId,
            folder_num,
            lang,
            id: id[1],
            language_from,
        }),
    ]);

    return data;
    // TODO add remove translated audio action
};

/** «+»: ответ сервера или отказ — словами и с кодом причины (no_speech, cue_timing_invalid…). */
export const createRusSubIntervalAction = async (
    params: CreateRusSubIntervalArgs,
): Promise<{ data?: CreateRusSubIntervalResponse; error?: string; reason?: string }> => {
    try {
        const data = await createRusSubIntervalApi(params);
        return { data };
    } catch (err) {
        return { error: errorHandler(err as Error), reason: errorReason(err) };
    }
};

export const createRusSubIntervalMultiAction = async (
    params: CreateRusSubIntervalMultiArgs,
): Promise<{ data?: CreateRusSubIntervalMultiResponse; error?: string }> => {
    let error = "create rus subtitle interval multi error";
    try {
        const data = await createRusSubIntervalMultiApi(params);
        return { data };
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error };
};

export const cutAudioWithoutPauseAction = async (params: CutAudioArgs) => {
    let error = null;
    let data: CutAudioResponse | null = null;
    try {
        data = await cutAudioWithoutPause(params);
    } catch (err) {
        error = errorHandler(err as Error);
    }
    return { error, data };
};
